import { useEffect, useMemo, useState } from "react";
import { fetchSheetViaProxy } from "../../../../lib/sheets";

const ARCHIVE_SHEET_ID = "1YX4CzZlUxNIheMi_BKSoJivlo9M3g9cpcdPmqwtWroQ";
const RANGE = "archive_s1_standings_marken!A1:Y100";

// "A" -> 1, "Z" -> 26, ...
const colIndex = (letter) => {
    let n = 0;
    for (let i = 0; i < letter.length; i++) n = n * 26 + (letter.charCodeAt(i) - 64);
    return n;
};

const LETTER_W  = colIndex("X");
const LETTER_I  = colIndex("J");
const LETTER_T  = colIndex("U");
const LETTER_U  = colIndex("V");
const LETTER_WW = colIndex("X");

const isNumeric = (v) => v !== null && v !== "" && !isNaN(Number(v));

function computeTop3ByColumn(rowsAsArrays, startCol, endCol) {
    const map = new Map();
    for (let c = startCol; c <= endCol; c++) {
        const nums = rowsAsArrays
            .map(arr => arr[c - 1])
            .filter(v => v !== null && v !== undefined && v !== "")
            .map(v => Number(v))
            .filter(v => !Number.isNaN(v));

        if (!nums.length) {
            map.set(c, null);
            continue;
        }

        const uniqDesc = Array.from(new Set(nums)).sort((a, b) => b - a);
        map.set(c, {
            gold: uniqDesc[0] ?? null,
            silver: uniqDesc[1] ?? null,
            bronze: uniqDesc[2] ?? null,
        });
    }
    return map;
}

export default function ArchiveSeason1Marken() {
    const [data, setData] = useState({ header: [], rows: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [noData, setNoData] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const res = await fetchSheetViaProxy({ range: RANGE, sheetId: ARCHIVE_SHEET_ID });
                setData(res);
            } catch (e) {
                const msg = e?.message || String(e);
                if (msg.includes("Unable to parse range")) {
                    setNoData(true);
                } else {
                    setError(msg);
                }
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const { visibleHeaders, rowsAsArrays, top3PerRaceColumn, completedRuns, idxX } = useMemo(() => {
        const headers = data.header || [];
        const visibleHeaders = headers.slice(0, LETTER_W);

        const idxX = colIndex("Y") - 1;
        const rowsAsArrays = (data.rows || []).map((r) => headers.map((h) => r[h]));
        const top3PerRaceColumn = computeTop3ByColumn(rowsAsArrays, LETTER_I, LETTER_T);

        let completedRuns = 0;
        for (let c = LETTER_I; c <= LETTER_T; c++) {
            const anyNumberInCol = rowsAsArrays.some(row => {
                const v = row[c - 1];
                return v !== null && v !== "" && !Number.isNaN(Number(v));
            });
            if (anyNumberInCol) completedRuns++;
        }

        return { visibleHeaders, rowsAsArrays, top3PerRaceColumn, completedRuns, idxX };
    }, [data]);

    if (loading) return <div className="card"><p>Lade Markenwertung...</p></div>;

    if (noData) {
        return (
            <div className="card">
                <h2>Archiv Season 1 <span className="pill pill--marke">MARKEN</span></h2>
                <p className="muted">
                    Für diese Archivansicht sind aktuell noch keine Markendaten vorhanden.
                </p>
            </div>
        );
    }

    if (error) {
        return <div className="card"><p style={{ color:"#f55" }}>Fehler: {error}</p></div>;
    }

    return (
        <>
            <div className="card">
                <h2>Archiv Season 1 <span className="pill pill--marke">MARKEN</span></h2>
                <p className="muted">
                    <strong>Inoffizielle</strong> Markenwertung der Season 1 nach Abschluss des {completedRuns}. Laufes. <br/>
                    Aus der Gesamtwertung jedes Laufes erhalten die ersten 15 Marken Punkte.
                    Die Anzahl der Fahrer ist die gesamte Zahl derer, die in der Season das Auto fahren. <br/>
                    Durch diese Unausgeglichenheit ist die Wertung inoffiziell.
                </p>
            </div>

            <div className="fancyTable">
                <table className="tbl">
                    <thead>
                    <tr>
                        {visibleHeaders.map((h, i) => {
                            const colNumber = i + 1;

                            const label =
                                colNumber === 1 ? "Pos" :
                                    colNumber === 2 ? "Δ" :
                                        colNumber === 3 ? "Logo" :
                                            colNumber === 4 ? "Marke" :
                                                h;

                            const isRace = colNumber >= LETTER_I && colNumber <= LETTER_T;
                            const raceLeft = colNumber === LETTER_I;
                            const raceRight = colNumber === LETTER_T;
                            const evenStripe = isRace && ((colNumber - LETTER_I) % 2 === 0);

                            const leftAligned = (colNumber === 1 || colNumber === 4);

                            const thClass = [
                                isRace ? "race-col" : "",
                                raceLeft ? "race-left" : "",
                                raceRight ? "race-right" : "",
                                evenStripe ? "race-alt" : "",
                                (colNumber === 3) ? "icon" : "",
                                leftAligned ? "align-left" : "",
                            ].filter(Boolean).join(" ");

                            return <th key={i} className={thClass}>{label}</th>;
                        })}
                    </tr>
                    </thead>

                    <tbody>
                    {rowsAsArrays.map((arr, ri) => (
                        <tr key={ri}>
                            {visibleHeaders.map((_, i) => {
                                const colNumber = i + 1;
                                const val = arr[i];

                                if (colNumber === 3) {
                                    const src = arr[idxX] || "";
                                    return (
                                        <td key={i} className="icon">
                                            {src ? <img src={src} alt="Brand" className="teamlogo lg" loading="lazy" /> : null}
                                        </td>
                                    );
                                }

                                const isRace = colNumber >= LETTER_I && colNumber <= LETTER_T;
                                const raceLeft = colNumber === LETTER_I;
                                const raceRight = colNumber === LETTER_T;
                                const evenStripe = isRace && ((colNumber - LETTER_I) % 2 === 0);

                                let tierClass = "";
                                if (isRace && val !== "" && val !== null && val !== undefined) {
                                    const numVal = Number(val);
                                    const t3 = top3PerRaceColumn.get(colNumber);
                                    if (t3 && !Number.isNaN(numVal)) {
                                        if (numVal === t3.gold) tierClass = "race-gold";
                                        else if (numVal === t3.silver) tierClass = "race-silver";
                                        else if (numVal === t3.bronze) tierClass = "race-bronze";
                                    }
                                }

                                const leftAligned = (colNumber === 1 || colNumber === 4);

                                let deltaClass = "";
                                if (colNumber === 2) {
                                    const n = Number(val);
                                    if (!isNaN(n)) {
                                        if (n > 0) deltaClass = "delta-up";
                                        else if (n < 0) deltaClass = "delta-down";
                                    }
                                }

                                const compactMetrics = (colNumber >= 5 && colNumber <= 8) ? "compact-metrics" : "";

                                const numeric =
                                    isNumeric(val) ||
                                    (colNumber >= 5 && colNumber <= 8) ||
                                    isRace ||
                                    (colNumber >= LETTER_U && colNumber <= LETTER_WW);

                                const tdClass = [
                                    numeric ? "num" : "",
                                    isRace ? "race-col" : "",
                                    raceLeft ? "race-left" : "",
                                    raceRight ? "race-right" : "",
                                    evenStripe ? "race-alt" : "",
                                    tierClass,
                                    leftAligned ? "align-left" : "",
                                    (colNumber === 5) ? "points-highlight" : "",
                                    (colNumber === 1) ? "pos-highlight" : "",
                                    deltaClass,
                                    compactMetrics,
                                ].filter(Boolean).join(" ");

                                return <td key={i} className={tdClass}>{val}</td>;
                            })}
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </>
    );
}